import * as React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Select from '@mui/material/Select';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { DesktopTimePicker } from '@mui/x-date-pickers/DesktopTimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimeValidationError } from '@mui/x-date-pickers/models';

import {
  useBookBySpotMutation,
  BookingOccasionEnum,
  BookingStatusEnum,
  useSpotSchedulesQuery,
} from '@graphql';
import { refetchBookingsVar } from '@context';
import { shouldDisableBookingDate, shouldDisableBookingTime } from '@utils';
import { useSession } from '@hooks';
import validationSchema from './validationSchema';
import { Form } from '../../form';

interface CreateBookingFormProps {
  handleClose: () => void;
  spotSlug: string;
  isOpen: boolean;
  isDisplayCalendar: boolean;
}

export default function CreateBookingForm(props: Readonly<CreateBookingFormProps>) {
  const { handleClose, isDisplayCalendar } = props;
  const [isDateClosed, setIsDateClosed] = React.useState(false);
  const [isFieldFocused, setIsFieldFocused] = React.useState(false);
  const [timeError, setTimeError] = React.useState<TimeValidationError | null>(null);
  const dateInputRef = React.useRef<HTMLInputElement | null>(null);
  const { account } = useSession();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation('booking');

  const { data } = useSpotSchedulesQuery({
    skip: !account?.slug,
    variables: { slug: account?.slug } as { slug: string },
  });

  const [createBooking] = useBookBySpotMutation({
    onCompleted() {
      refetchBookingsVar(true);
      toast.success(t('update.success'));
    },
    onError() {
      toast.error(t('update.error'));
    },
  });

  const getInitalValues = () => {
    const focusDate = searchParams.get('date') ?? new Date().toISOString().split('T')[0];
    const focusMoment = moment(focusDate).startOf('day');

    // now + 1h, no matter the date chosen
    const nowPlusOne = moment().add(1, 'hour').set({ minute: 0, second: 0, millisecond: 0 });

    // const startTime = moment(focusDate).add(1, 'hour').set({ minute: 0, second: 0, millisecond: 0 });
    const startTime = focusMoment.clone().set({
      hour: nowPlusOne.hour(),
      minute: nowPlusOne.minute(),
      second: 0,
      millisecond: 0,
    });

    const defaultValues = {
      startDate: focusMoment,
      startTime: startTime,
      participants: '2',
      occasion: '',
      status: BookingStatusEnum.Accepted,
      firstname: '',
      lastname: '',
      phone: '',
      email: '',
    };

    return defaultValues;
  };

  const formik = useFormik({
    initialValues: getInitalValues(),
    validationSchema: validationSchema(null, t, account?.__typename),
    validateOnMount: false,
    onSubmit: async (values) => {
      const booking = {
        client: {
          firstname: values.firstname,
          lastname: values.lastname.length ? values.lastname : undefined,
          email: values.email.length ? values.email : undefined,
          phone: values.phone,
        },
        date: values.startDate.format('YYYY-MM-DD'),
        time: values.startTime.format('HH:mm:ss'),
        startTime: values.startTime.toDate(),
        participants: +values.participants,
        occasion: values.occasion.length ? (values.occasion as BookingOccasionEnum) : undefined,
        status: values.status,
      };

      await createBooking({ variables: { input: booking } });

      handleClose();
    },
  });

  React.useEffect(() => {
    const isClosed = data?.spotSchedules
      ? shouldDisableBookingDate(
          formik.values.startDate,
          data?.spotSchedules.opening,
          data?.spotSchedules.closing
        )
      : true;

    setIsDateClosed(isClosed);
  }, [formik.values.startDate]);

  React.useEffect(() => {
    if (isDateClosed) {
      setTimeError(null);
      formik.resetForm();
    }
  }, [isDateClosed]);

  const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (['Enter', 'Escape'].includes(event.key)) {
      // remove focus on mobile
      (event.target as HTMLElement).blur();
    }
  };

  const handleChangeDate = (value: Moment | null) => {
    if (value) {
      formik.setFieldValue('startDate', value.startOf('day'));

      const startTime = moment(formik.values.startTime).set({
        year: value.year(),
        month: value.month(),
        date: value.date(),
      });

      formik.setFieldValue('startTime', startTime);
    }
  };

  const handleShouldDisableDate =
    (data: ReturnType<typeof useSpotSchedulesQuery>['data']) => (date: Moment) => {
      return data?.spotSchedules
        ? shouldDisableBookingDate(date, data?.spotSchedules.opening, data?.spotSchedules.closing)
        : false;
    };

  const errorMessage = React.useMemo(() => {
    if (isDateClosed) {
      return null;
    } else if (timeError === 'shouldDisableTime-hours') {
      return 'Le spot est fermé à cette heure là';
    } else if (timeError === 'disablePast') {
      return 'Cette heure pour cette date est dépassée';
    }
    return null;
  }, [timeError]);

  if (account?.__typename !== 'Spot') {
    return null;
  }

  return (
    <Form>
      <Form.Header
        type="booking"
        action="create"
        startDate={moment(formik.values.startDate)}
        isDateClosed={false}
        handleClose={handleClose}
      />
      <form style={{ flexGrow: 1, overflowY: 'auto' }}>
        <Form.Body>
          <FormControl fullWidth error={!!formik.errors.startDate}>
            {isDisplayCalendar ? (
              <DateCalendar
                disablePast
                timezone="Europe/Paris"
                value={formik.values.startDate}
                onChange={handleChangeDate}
                shouldDisableDate={handleShouldDisableDate(data)}
                sx={{ width: '100%' }}
              />
            ) : (
              <DatePicker
                label="Date de réservation"
                disablePast
                timezone="Europe/Paris"
                value={formik.values.startDate}
                onChange={handleChangeDate}
                shouldDisableDate={handleShouldDisableDate(data)}
                sx={{ width: '100%' }}
                slotProps={{
                  textField: {
                    inputRef: dateInputRef,
                  },
                }}
                onClose={() => {
                  // known issue to avoid Mui focusing back the input after closing the picker
                  // First MUI refocus the field, then remove blur
                  setTimeout(() => {
                    dateInputRef.current?.blur();
                  }, 0);
                }}
              />
            )}
            {isDateClosed && (
              <Alert severity="warning" icon={false} sx={{ mt: 1 }}>
                {t('alert.spot is closed this day', { ns: 'booking', spot: data?.spotSchedules.name })}
              </Alert>
            )}
          </FormControl>
          <DesktopTimePicker
            name="startTime"
            label="Heure d'arrivée"
            // handle "disablePast" in shouldDisableTime()
            // disablePast => do not use as it is based on current date, not on selected date
            closeOnSelect
            timezone="Europe/Paris"
            referenceDate={formik.values.startDate}
            value={formik.values.startTime}
            ampm={false}
            onError={(newError) => setTimeError(newError)}
            skipDisabled
            disabled={isDateClosed}
            timeSteps={{ hours: 1, minutes: 15, seconds: 60 }}
            slotProps={{
              textField: {
                helperText: errorMessage,
                error: !isDateClosed && !!timeError,
              },
            }}
            onChange={(value) => {
              if (value) {
                const dateTime = moment(value).set({
                  year: formik.values.startDate.year(),
                  month: formik.values.startDate.month(),
                  date: formik.values.startDate.date(),
                });

                formik.setFieldValue('startTime', dateTime);
              }
            }}
            shouldDisableTime={(date) => {
              if (!data?.spotSchedules || !formik.values.startDate) {
                return false;
              }

              const dateTime = moment(date).set({
                year: formik.values.startDate.year(),
                month: formik.values.startDate.month(),
                date: formik.values.startDate.date(),
              });

              // 1) if the booking date is today, disable past hours
              const now = moment();
              if (dateTime.isSame(now, 'day') && dateTime.isBefore(now)) {
                return true;
              }

              // 2) if not apply your opening/closing logic
              return shouldDisableBookingTime(
                dateTime,
                data?.spotSchedules.opening,
                data?.spotSchedules.closing
              );
            }}
          />
          <TextField
            {...formik.getFieldProps('phone')}
            required
            fullWidth
            variant="outlined"
            slotProps={{ input: { min: 1 } }}
            name="phone"
            type="tel"
            label={t('mobilePhone.label', { ns: 'field' })}
            onFocus={() => setIsFieldFocused(true)}
            onBlur={() => setIsFieldFocused(false)}
            onChange={formik.handleChange}
            disabled={isDateClosed}
            error={formik.touched.phone && Boolean(formik.errors.phone)}
            helperText={formik.touched.phone && formik.errors.phone ? String(formik.errors.phone) : null}
          />
          <TextField
            {...formik.getFieldProps('firstname')}
            required
            fullWidth
            variant="outlined"
            slotProps={{ input: { min: 1 } }}
            name="firstname"
            label={t('firstname.label', { ns: 'field' })}
            onFocus={() => setIsFieldFocused(true)}
            onBlur={() => setIsFieldFocused(false)}
            onKeyDown={handleOnKeyDown}
            onChange={formik.handleChange}
            disabled={isDateClosed}
            error={formik.touched.firstname && Boolean(formik.errors.firstname)}
            helperText={
              formik.touched.firstname && formik.errors.firstname ? String(formik.errors.firstname) : null
            }
          />
          <TextField
            {...formik.getFieldProps('lastname')}
            fullWidth
            variant="outlined"
            slotProps={{ input: { min: 1 } }}
            name="lastname"
            label={t('lastname.label', { ns: 'field' })}
            onFocus={() => setIsFieldFocused(true)}
            onBlur={() => setIsFieldFocused(false)}
            onKeyDown={handleOnKeyDown}
            onChange={formik.handleChange}
            disabled={isDateClosed}
            error={formik.touched.lastname && Boolean(formik.errors.lastname)}
            helperText={
              formik.touched.lastname && formik.errors.lastname ? String(formik.errors.lastname) : null
            }
          />
          <TextField
            {...formik.getFieldProps('email')}
            fullWidth
            variant="outlined"
            slotProps={{ input: { min: 1 } }}
            name="email"
            label={t('email.label', { ns: 'field' })}
            onFocus={() => setIsFieldFocused(true)}
            onBlur={() => setIsFieldFocused(false)}
            onKeyDown={handleOnKeyDown}
            onChange={formik.handleChange}
            disabled={isDateClosed}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email ? String(formik.errors.email) : null}
          />
          <TextField
            {...formik.getFieldProps('participants')}
            required
            fullWidth
            variant="outlined"
            slotProps={{ input: { min: 1 } }}
            type="number"
            id="book-participants"
            name="participants"
            label={t('participants.label', { ns: 'field' })}
            disabled={isDateClosed}
            onFocus={() => setIsFieldFocused(true)}
            onBlur={() => setIsFieldFocused(false)}
            onKeyDown={handleOnKeyDown}
            onChange={(e) => {
              const value = Math.max(1, parseInt(e.target.value, 10));
              formik.setFieldValue('participants', isNaN(value) ? '' : value);
            }}
            error={formik.touched.participants && Boolean(formik.errors.participants)}
            helperText={
              formik.touched.participants && formik.errors.participants
                ? String(formik.errors.participants)
                : null
            }
          />
          <FormControl fullWidth>
            <InputLabel id="status-select-label" disabled={isDateClosed}>
              Status
            </InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={formik.values.status}
              name="status"
              label="Status"
              disabled={isDateClosed}
              onFocus={() => setIsFieldFocused(true)}
              onBlur={() => setIsFieldFocused(false)}
              onChange={formik.handleChange}
            >
              {[BookingStatusEnum.Accepted, BookingStatusEnum.Pending].map((status) => (
                <MenuItem key={status} value={status}>
                  {t(`status.${status}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Select
              {...formik.getFieldProps('occasion')}
              labelId="occasion-select-label"
              name="occasion"
              autoComplete="off"
              disabled={isDateClosed}
              displayEmpty
            >
              <MenuItem value="">
                <em>{t('occasion select.placeholder', { ns: 'field' })}</em>
              </MenuItem>
              {Object.entries(BookingOccasionEnum).map(([key, value]) => (
                <MenuItem key={key} value={value}>
                  {t(`suitable.${key.charAt(0).toLowerCase() + key.slice(1)}`, {
                    ns: 'spot',
                  })}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText id="occasion-helper-text">{t('word.Optional', { ns: 'common' })}</FormHelperText>
          </FormControl>
        </Form.Body>
      </form>
      <Form.Footer hide={isFieldFocused}>
        <Button
          disabled={!formik.dirty || !formik.isValid}
          variant="contained"
          color="success"
          fullWidth
          onClick={() => formik.handleSubmit()}
        >
          {t('save', { ns: 'cta' })}
        </Button>
      </Form.Footer>
    </Form>
  );
}
